/**
 * Shared behaviour of the drawer and the workspace chat: creates the
 * conversation on the first message, sends the current screen context and
 * profile, stops runs and undoes whole runs.
 */
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAgent } from './AgentProvider';
import { agentApi } from './agentApi';
import { useAgentSession } from './useAgentSession';

export function useConversationController({ sessionId, setSessionId, originTab }) {
  const agent = useAgent();
  const agentSession = useAgentSession(sessionId);
  const pageContext = agent?.pageContext;
  const profileId = agent?.profileId || null;

  const send = useCallback(
    async (text) => {
      try {
        if (!sessionId) {
          const { session } = await agentApi.createSession({ originTab, profileId: profileId || undefined });
          await agentApi.sendMessage(session.id, { text, pageContext, profileId });
          setSessionId(session.id);
          return;
        }
        await agentSession.send(text, { pageContext, profileId });
      } catch (error) {
        toast.error(error.message);
      }
    },
    [sessionId, setSessionId, originTab, pageContext, profileId, agentSession],
  );

  const stop = useCallback(async () => {
    if (!agentSession.activeRun) return;
    try {
      await agentSession.cancel(agentSession.activeRun.id);
    } catch (error) {
      toast.error(error.message);
    }
  }, [agentSession]);

  const undoRun = useCallback(
    async (runId) => {
      try {
        const { outcomes } = await agentSession.undoRun(runId);
        const refused = outcomes.filter((outcome) => !outcome.ok);
        if (refused.length) toast.warn(`${outcomes.length - refused.length} undone, ${refused.length} refused: ${refused[0].error}`);
        else toast.success(`${outcomes.length} action${outcomes.length === 1 ? '' : 's'} undone`);
      } catch (error) {
        toast.error(error.message);
      }
    },
    [agentSession],
  );

  const status = agentSession.session?.status;
  return {
    agentSession,
    send,
    stop,
    undoRun,
    running: status === 'RUNNING',
    awaiting: status === 'AWAITING_APPROVAL',
    readOnly: agentSession.readOnly,
  };
}
